// src/lib/margin.ts
//
// Pure, computed-on-read margin from line-item unit_price/qty/unit_cost. Margin
// is NEVER persisted — no margin column, no trigger. STAFF-ONLY: cost (and thus
// margin) must never reach a customer/vendor surface.
//
// Null-cost semantics (the load-bearing rule): unit_cost === null means "cost
// not entered / margin unknown" — NOT zero cost, NOT 100% margin. A null-cost
// line is non-computable (lineMargin → null) and is EXCLUDED from job rollups
// (both its revenue and its cost), so a half-costed job never reports a falsely
// precise (overstated) margin. jobMargin flags incompleteness via
// hasUnknownCosts / allUnknown so the UI can label it "partial" / "unknown".
// ============================================================================

export type LineMargin = {
  revenue: number
  cost: number
  margin: number
  marginPct: number | null   // null when revenue is 0 (undefined ratio)
}

/** Per-line margin. Returns null when unit_cost is null/undefined (unknown). */
export function lineMargin(
  unitPrice: number,
  qty: number,
  unitCost: number | null | undefined,
): LineMargin | null {
  if (unitCost == null) return null
  const revenue = (Number(unitPrice) || 0) * (Number(qty) || 0)
  const cost = (Number(unitCost) || 0) * (Number(qty) || 0)
  const margin = revenue - cost
  const marginPct = revenue === 0 ? null : margin / revenue
  return { revenue, cost, margin, marginPct }
}

export type JobMargin = {
  revenue: number          // from known-cost lines ONLY
  cost: number             // from known-cost lines ONLY
  margin: number           // revenue - cost (known-cost lines)
  marginPct: number | null
  hasUnknownCosts: boolean // ≥1 line has null cost → total is PARTIAL
  allUnknown: boolean      // NO line has a known cost → margin entirely unknown
  knownLineCount: number
  lineCount: number
}

/**
 * Roll up job margin from its lines. Null-cost lines are excluded from both
 * revenue and cost (never treated as 0) so the result is the true margin of the
 * KNOWN-cost portion. hasUnknownCosts/allUnknown report completeness.
 */
export function jobMargin(
  lines: Array<{ unit_price: number; qty: number; unit_cost: number | null }>,
): JobMargin {
  let revenue = 0
  let cost = 0
  let knownLineCount = 0
  let hasUnknownCosts = false
  for (const l of lines) {
    if (l.unit_cost == null) { hasUnknownCosts = true; continue }
    revenue += (Number(l.unit_price) || 0) * (Number(l.qty) || 0)
    cost += (Number(l.unit_cost) || 0) * (Number(l.qty) || 0)
    knownLineCount++
  }
  const margin = revenue - cost
  const allUnknown = knownLineCount === 0
  const marginPct = (allUnknown || revenue === 0) ? null : margin / revenue
  return { revenue, cost, margin, marginPct, hasUnknownCosts, allUnknown, knownLineCount, lineCount: lines.length }
}

/** Format a margin fraction (0.42) as a percent string ('42%'), or '—' if null. */
export function formatMarginPct(pct: number | null): string {
  if (pct == null) return '—'
  return `${Math.round(pct * 100)}%`
}

// ─── Slices 2 + 3: vendor + labor cost as JOB-LEVEL addends ──────────────────
// Three cost dimensions roll into job margin:
//   line_cost (slice 1, per-line materials) + vendor_actual (slice 2, the sub's
//   bill) + labor_cost (slice 3, in-house labor) — both vendor and labor are
//   JOB-LEVEL addends, not per-line:
//     margin = line_revenue − (line_cost + vendor_actual + labor_cost).
//
// "Unknown" vs "N/A" — kept honest, and they differ per dimension:
//   • line costs unknown — some lines have null unit_cost (slice-1) → PARTIAL.
//   • vendor cost unknown — the job HAS vendor assignments but ZERO bills landed
//     (expected + missing) → PARTIAL. A job with NO assignments → vendor N/A,
//     NOT flagged. vendorActual is null only when zero bills exist.
//   • labor cost — ALWAYS N/A-when-absent, NEVER "unknown". Every job has a tech,
//     so flagging "no labor entered" would mark essentially every job partial =
//     noise. So zero labor entries contribute 0 to cost and raise NO partial flag.
//     laborCost is null (or 0) when no labor recorded; a positive value adds to cost.

export type JobMarginFull = {
  revenue: number              // line revenue from known-cost lines (slice-1 basis)
  lineCost: number             // known line costs
  vendorActual: number | null  // SUM of vendor bills, or null when none received
  laborCost: number | null     // SUM of field_job_labor, or null when none recorded (N/A)
  totalCost: number            // lineCost + (vendorActual||0) + (laborCost||0)
  margin: number               // revenue − totalCost
  marginPct: number | null
  lineCostsPartial: boolean    // ≥1 line has null cost
  vendorCostUnknown: boolean   // has vendor assignments but no bills yet
  isPartial: boolean           // lineCostsPartial OR vendorCostUnknown (labor NEVER contributes)
  bothCostTypes: boolean       // ≥1 costed line AND vendorActual > 0 → double-count warning (labor not involved)
  hasVendorAssignments: boolean
}

/**
 * Combine slice-1 line margin with job-level vendor cost (slice 2) and labor cost
 * (slice 3).
 * @param lines        the job's line items (unit_price, qty, unit_cost)
 * @param vendorActual SUM(vendor_bills.amount), or null when zero bills (unknown
 *                     IFF the job is subbed — see opts.hasVendorAssignments).
 * @param laborCost    SUM(field_job_labor.amount), or null when none recorded.
 *                     N/A-when-absent: contributes 0 and raises NO flag (labor is
 *                     never "unknown", unlike a subbed-but-unbilled job's vendor).
 * @param opts.hasVendorAssignments whether the job has ANY vendor assignment.
 */
export function jobMarginFull(
  lines: Array<{ unit_price: number; qty: number; unit_cost: number | null }>,
  vendorActual: number | null,
  laborCost: number | null,
  opts: { hasVendorAssignments: boolean },
): JobMarginFull {
  const base = jobMargin(lines) // line-side revenue/cost from known-cost lines only
  const vendorKnown = vendorActual != null
  const vendorCostUnknown = opts.hasVendorAssignments && !vendorKnown
  const totalCost = base.cost + (vendorKnown ? vendorActual! : 0) + (laborCost ?? 0)
  const margin = base.revenue - totalCost
  const marginPct = base.revenue === 0 ? null : margin / base.revenue
  const lineCostsPartial = base.hasUnknownCosts
  return {
    revenue: base.revenue,
    lineCost: base.cost,
    vendorActual,
    laborCost,
    totalCost,
    margin,
    marginPct,
    lineCostsPartial,
    vendorCostUnknown,
    // labor NEVER contributes to isPartial (N/A-when-absent, not unknown)
    isPartial: lineCostsPartial || vendorCostUnknown,
    bothCostTypes: base.knownLineCount > 0 && vendorKnown && vendorActual! > 0,
    hasVendorAssignments: opts.hasVendorAssignments,
  }
}

// ─── Slice 4: frozen-margin READ from invoice_cost_snapshots ─────────────────
// At job→invoice conversion the cost components are FROZEN into a snapshot row
// (revenue + line_cost + vendor_actual + labor_cost + the partial flags). At
// read time we only have those NUMBERS (not the raw lines jobMarginFull needs),
// so we recompute margin DIRECTLY from the frozen primitives — using the SAME
// formulas jobMarginFull uses internally, so the frozen number equals what the
// job-side margin showed at conversion. An incomplete-at-conversion snapshot
// (some line cost unknown, or subbed-but-unbilled) stays honest as "partial".

export type SnapshotMarginInput = {
  revenue: number
  line_cost: number
  vendor_actual: number | null
  labor_cost: number | null
  line_costs_partial: boolean
  vendor_cost_unknown: boolean
}

export type SnapshotMargin = {
  totalCost: number
  margin: number
  marginPct: number | null
  isPartial: boolean
  showable: boolean
  partialReason: string
}

export function marginFromSnapshot(snap: SnapshotMarginInput): SnapshotMargin {
  const totalCost = snap.line_cost + (snap.vendor_actual ?? 0) + (snap.labor_cost ?? 0)
  const margin = snap.revenue - totalCost
  const marginPct = snap.revenue === 0 ? null : margin / snap.revenue
  const isPartial = snap.line_costs_partial || snap.vendor_cost_unknown // labor never flags partial
  const showable = !(snap.revenue === 0 && snap.vendor_actual == null && snap.labor_cost == null)
  // Mirror JobLineItemsEditor's partialReason wording verbatim.
  const partialReason = snap.line_costs_partial && snap.vendor_cost_unknown
    ? 'line + vendor costs incomplete'
    : snap.vendor_cost_unknown
      ? 'vendor bill not received'
      : 'some line costs missing'
  return { totalCost, margin, marginPct, isPartial, showable, partialReason }
}
